import { useState, useRef } from "react";
import { toast } from "sonner";
import { Trash2, FileDown, Minus, Plus, ImageOff, Mail, Mic, Square, Loader2, Check, AlertTriangle } from "lucide-react";
import { api, imgUrl, errDetail, unitOf, unitsTotal, lineTotal } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const VOICE_ENABLED = false;

export default function CartPage() {
  const { items, setQty, clear, total, refresh } = useCart();
  const hasOutOfStock = items.some((it) => it.product.out_of_stock);
  const removeOutOfStock = async () => {
    try {
      const { data } = await api.delete("/cart/out-of-stock");
      await refresh();
      toast.success(`${data.removed} ${t("cart.removedOos")}`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const { t } = useLang();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [traspaso, setTraspaso] = useState("");
  const [marca, setMarca] = useState("");

  const traspasoCost = ["credito", "cod"].includes(user?.client_type) ? 30 : 20;
  const grandTotal = total + traspasoCost;

  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        await sendVoice(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono. Revise los permisos del navegador.");
    }
  };

  const stopRec = () => { try { mediaRef.current?.stop(); } catch {} setRecording(false); };

  const sendVoice = async (blob) => {
    setVoiceBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "pedido.webm");
      const { data } = await api.post("/voice/parse-order", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if ((data.matched || []).length === 0 && (data.unmatched || []).length === 0) {
        toast.error("No se entendió el pedido. Intente de nuevo.");
      }
      setVoiceResult({ ...data, matched: (data.matched || []).map((m) => ({ ...m })) });
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setVoiceBusy(false);
    }
  };

  const setVoiceQty = (i, v) => {
    const q = Math.max(1, parseInt(v || "1", 10) || 1);
    setVoiceResult((r) => ({ ...r, matched: r.matched.map((m, idx) => (idx === i ? { ...m, qty_boxes: q } : m)) }));
  };
  const removeVoiceItem = (i) => setVoiceResult((r) => ({ ...r, matched: r.matched.filter((_, idx) => idx !== i) }));

  const confirmVoiceOrder = async () => {
    try {
      for (const m of voiceResult.matched) {
        const existing = items.find((it) => it.product.id === m.product_id);
        await setQty(m.product_id, (existing?.qty_boxes || 0) + m.qty_boxes);
      }
      await refresh();
      toast.success(`${voiceResult.matched.length} producto(s) agregados a la proforma`);
      setVoiceResult(null);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/proforma/email", { notes, traspaso, marca });
      toast.success(`Proforma ${data.number} ${t("toast.sentTo")} ${data.sent_to}`);
      setNotes("");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setEmailBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.post("/proforma", { notes, traspaso, marca }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforma_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast.pdfGenerated"));
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="cart-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">{t("cart.title")}</h1>
        {items.length > 0 && (
          <div className="flex items-center gap-3">
            {hasOutOfStock && (
              <button onClick={removeOutOfStock} data-testid="remove-out-of-stock-button" className="text-xs font-semibold text-red-600 flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-400 hover:bg-red-500/10">
                <AlertTriangle size={13} /> {t("cart.removeOos")}
              </button>
            )}
            <button onClick={clear} data-testid="clear-cart-button" className="text-xs text-red-500 hover:underline flex items-center gap-1">
              <Trash2 size={13} /> {t("cart.clear")}
            </button>
          </div>
        )}
      </div>

      {VOICE_ENABLED && (
      <div className="bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="voice-order-card">
        <div className="flex-1">
          <p className="font-semibold text-sm flex items-center gap-2"><Mic size={16} className="text-amber-600" /> Pedido por voz</p>
          <p className="text-xs text-muted-foreground">Presione y dicte su pedido: cantidades, nombres o referencias. Ej: "3 cajas de toallas y 2 de la referencia 20".</p>
        </div>
        {!recording ? (
          <button onClick={startRec} disabled={voiceBusy} data-testid="voice-record-button"
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50">
            {voiceBusy ? <><Loader2 size={16} className="animate-spin" /> Procesando...</> : <><Mic size={16} /> Dictar pedido</>}
          </button>
        ) : (
          <button onClick={stopRec} data-testid="voice-stop-button"
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors animate-pulse">
            <Square size={15} /> Detener y procesar
          </button>
        )}
      </div>
      )}

      <Dialog open={!!voiceResult} onOpenChange={(o) => !o && setVoiceResult(null)}>
        <DialogContent data-testid="voice-result-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Pedido interpretado</DialogTitle></DialogHeader>
          {voiceResult && (
            <div className="space-y-4">
              {voiceResult.transcript && (
                <p className="text-xs text-muted-foreground italic border-l-2 border-amber-500 pl-2" data-testid="voice-transcript">"{voiceResult.transcript}"</p>
              )}
              {voiceResult.matched.length > 0 ? (
                <div className="space-y-2" data-testid="voice-matched-list">
                  {voiceResult.matched.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 border border-border rounded-xl p-2" data-testid={`voice-match-${i}`}>
                      {m.image_url ? <img src={imgUrl(m.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center"><ImageOff size={16} /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono text-amber-600 uppercase">{m.reference}</p>
                        <p className="text-sm font-medium leading-tight truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground">${(m.price_dozen || 0).toFixed(2)}/{m.unit}</p>
                      </div>
                      <div className="flex items-center border border-border rounded-full">
                        <button onClick={() => setVoiceQty(i, m.qty_boxes - 1)} className="p-1.5 hover:bg-muted rounded-l-full"><Minus size={13} /></button>
                        <input value={m.qty_boxes} onChange={(e) => setVoiceQty(i, e.target.value)} data-testid={`voice-qty-${i}`}
                          className="w-10 text-center text-sm font-semibold bg-transparent focus:outline-none" />
                        <button onClick={() => setVoiceQty(i, m.qty_boxes + 1)} className="p-1.5 hover:bg-muted rounded-r-full"><Plus size={13} /></button>
                      </div>
                      <button onClick={() => removeVoiceItem(i)} className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-full" data-testid={`voice-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No se identificaron productos del catálogo.</p>
              )}
              {voiceResult.unmatched.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-1" data-testid="voice-unmatched-list">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> No encontrados (corrija manualmente):</p>
                  {voiceResult.unmatched.map((u, i) => <p key={i} className="text-xs text-muted-foreground">• {u}</p>)}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setVoiceResult(null)} className="flex-1 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-muted" data-testid="voice-cancel-button">Cancelar</button>
                <button onClick={confirmVoiceOrder} disabled={voiceResult.matched.length === 0} data-testid="voice-confirm-button"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold disabled:opacity-50">
                  <Check size={16} /> Agregar a la proforma
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="cart-empty-state">{t("cart.empty")}</div>
      ) : (
        <>
          {hasOutOfStock && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 rounded-2xl px-4 py-3 text-sm font-medium" data-testid="cart-out-of-stock-warning">
              <AlertTriangle size={18} /> {t("cart.outOfStockWarn")}
            </div>
          )}
          {/* Desktop table */}
          <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm" data-testid="cart-table">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">{t("cart.product")}</th>
                  <th className="px-4 py-3 font-semibold text-center">{t("cart.boxes")}</th>
                  <th className="px-4 py-3 font-semibold text-center">{t("cart.totalQty")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("cart.pricePer")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("cart.total")}</th>
                  <th className="px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ product: p, qty_boxes }) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`cart-row-${p.reference}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center"><ImageOff size={16} /></div>}
                        <div>
                          <span className="text-[10px] font-mono text-amber-600 uppercase">{p.reference} · {p.category}</span>
                          <p className="font-medium">{p.name} {p.out_of_stock && <span className="ml-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider align-middle" data-testid={`cart-out-of-stock-${p.reference}`}>{t("product.outOfStock")}</span>}</p>
                          <p className="text-xs text-muted-foreground">{p.packing > 0 ? `${p.packing} ${unitOf(p)}/caja` : t("cart.packingNA")}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <QtyControl p={p} qty={qty_boxes} setQty={setQty} />
                    </td>
                    <td className="px-4 py-3 text-center font-semibold" data-testid={`cart-units-${p.reference}`}>
                      {unitsTotal(p, qty_boxes)} <span className="text-xs font-normal text-muted-foreground">{unitOf(p)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">${p.price_dozen.toFixed(2)}<span className="text-xs text-muted-foreground">/{unitOf(p)}</span></td>
                    <td className="px-4 py-3 text-right font-semibold" data-testid={`cart-line-total-${p.reference}`}>${lineTotal(p, qty_boxes).toFixed(2)}</td>
                    <td className="px-2 py-3">
                      <button onClick={() => setQty(p.id, 0)} data-testid={`remove-item-${p.reference}`} className="p-2 text-red-500 hover:bg-red-500/10 rounded-full"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-3">
            {items.map(({ product: p, qty_boxes }) => (
              <div key={p.id} className="bg-card border border-border rounded-2xl p-3 flex gap-3" data-testid={`cart-card-${p.reference}`}>
                {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-20 h-20 rounded-xl object-cover" /> : <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center"><ImageOff size={18} /></div>}
                <div className="flex-1 min-w-0 space-y-1">
                  <span className="text-[10px] font-mono text-amber-600 uppercase">{p.reference}</span>
                  <p className="font-medium text-sm leading-tight">{p.name} {p.out_of_stock && <span className="ml-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">{t("product.outOfStock")}</span>}</p>
                  <p className="text-xs text-muted-foreground">
                    {qty_boxes} {t("product.boxes")} = <b>{unitsTotal(p, qty_boxes)} {unitOf(p)}</b> · ${p.price_dozen.toFixed(2)}/{unitOf(p)}
                  </p>
                  <p className="text-sm font-bold">{t("cart.total")}: ${lineTotal(p, qty_boxes).toFixed(2)}</p>
                  <div className="flex items-center justify-between pt-1">
                    <QtyControl p={p} qty={qty_boxes} setQty={setQty} />
                    <button onClick={() => setQty(p.id, 0)} className="p-2 text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">{t("cart.traspasoLabel")}</label>
                <input value={traspaso} onChange={(e) => setTraspaso(e.target.value)} maxLength={120}
                  data-testid="proforma-traspaso-input"
                  className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t("cart.marcaLabel")}</label>
                <input value={marca} onChange={(e) => setMarca(e.target.value)} maxLength={120}
                  data-testid="proforma-marca-input"
                  className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t("cart.notesLabel")}</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500}
                data-testid="proforma-notes-input"
                placeholder={t("cart.notesPlaceholder")}
                className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-xs text-muted-foreground" data-testid="cart-traspaso-cost">{t("cart.traspaso")}: ${traspasoCost.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">{t("cart.grandTotal")}</p>
                <p className="text-3xl font-bold font-heading" data-testid="cart-grand-total">${grandTotal.toFixed(2)}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button onClick={sendEmail} disabled={emailBusy || hasOutOfStock} data-testid="send-proforma-email-button"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-amber-600 text-amber-600 font-semibold hover:bg-amber-600/10 transition-colors disabled:opacity-50">
                  <Mail size={18} /> {emailBusy ? t("cart.sending") : t("cart.sendEmail")}
                </button>
                <button onClick={downloadPdf} disabled={busy || hasOutOfStock} data-testid="generate-proforma-pdf-button"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50">
                  <FileDown size={18} /> {busy ? t("cart.generating") : t("cart.generatePdf")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function QtyControl({ p, qty, setQty }) {
  const { t } = useLang();
  return (
    <div className="flex flex-col items-center mx-auto w-fit">
      <div className="flex items-center border border-border rounded-full">
        <button onClick={() => setQty(p.id, Math.max(0, qty - 1))} className="p-1.5 hover:bg-muted rounded-l-full" data-testid={`cart-qty-minus-${p.reference}`}><Minus size={13} /></button>
        <span className="px-3 text-sm font-semibold" data-testid={`cart-qty-${p.reference}`}>{qty}</span>
        <button onClick={() => setQty(p.id, qty + 1)} className="p-1.5 hover:bg-muted rounded-r-full" data-testid={`cart-qty-plus-${p.reference}`}><Plus size={13} /></button>
      </div>
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{t("product.boxes")}</span>
    </div>
  );
}
